import React, {useState} from "react";
import './ExpandableTable.css';
export default function ExpandableTable ( { children }) {
    return (
        <div className="table-responsive">
            <table className="table table-stripe table-hover skynet-expandable-table">
                {children}
            </table>
        </div>
    )
}

function Expandable({ children, isExpanded, toggle, isExpandable, colSpan = 1, level = 0 }) {
    const padding = level > 0 ? 7.2 * Math.pow(2, level - 1) : 1.6;
    // Apply padding to first td child using the same helper
    const paddedChildren = applyPaddingToFirstTd(children, level);
    const childrenArray = React.Children.toArray(paddedChildren);

    // Add bold to all td children if expanded
    const modifiedChildren = isExpanded && level > 0
        ? childrenArray.map(child => {
            if (typeof child.type === 'string' && child.type.toLowerCase() === 'td') {
                const originalClassName = child.props.className || "";
                return React.cloneElement(child, {
                    className: `${originalClassName} bold`.trim()
                });
            }
            return child;
        })
        : childrenArray;
    
    if (!isExpandable) {
        return <tr className="non-expandable">{paddedChildren}</tr>;
    }
    const firstTdChild = modifiedChildren.find(child => child.type === 'td');
    const columns = modifiedChildren.filter(child => child !== firstTdChild);
    const tdChildren = firstTdChild ? React.Children.toArray(firstTdChild.props.children) : [];

    // Merge className from original td
    const originalClassName = firstTdChild?.props?.className || "";
    const caretClass = `skynet-caret-toggle${isExpanded ? " toggle bold" : ""}`;
    const mergedClassName = `${caretClass} ${originalClassName}`.trim();

    // Merge other props if needed (e.g., data-*)
    const otherTdProps = firstTdChild ? Object.fromEntries(Object.entries(firstTdChild.props).filter(([key]) => key !== 'children' && key !== 'className' && key !== 'style' && key !== 'colSpan')) : {};

    return (
        <tr onClick={toggle} className={`expandable-row cursorpointer${isExpanded ? ' active' :  ""}`}> 
            <td
                className={mergedClassName}
                colSpan={colSpan}
                style={{ paddingLeft: `${padding}rem` }}
                {...otherTdProps}
            >
                <span className="caret mr-12 mb-2"></span>
                {tdChildren}
            </td>
            {columns && columns}
        </tr>
    );
}

function Row({ children, level = 0 }) {
    const [isExpanded, setIsExpanded] = useState(false);

    const childrenArray = React.Children.toArray(children);
    const expandableRow = childrenArray.find(child => child.type === Expandable);
    const expandedContent = childrenArray.find(child => child.type === Expanded);
    let expandableRowContent = null;
    let isExpandable = !expandedContent?.props?.children || !(expandedContent?.props?.children?.length === 0)

    const customOnClick = expandableRow?.props?.onClick;
    const toggle = () => {
        if (!isExpanded && typeof customOnClick === 'function') {
            customOnClick();
        }
        setIsExpanded(exp => !exp);
    };

    if (expandableRow) {
        expandableRowContent = React.cloneElement(expandableRow, { isExpanded, toggle, isExpandable: isExpandable, level });
    }
    return (<>
        {expandableRowContent}
        {isExpanded && expandedContent && React.cloneElement(expandedContent, { level: level + 1 })}
    </>)
}

function applyPaddingToFirstTd(children, level, found = { value: false }) {
    return React.Children.map(children, child => {
        if (!child) return child;
        if (found.value) {
            if (child.props && child.props.children) {
                return React.cloneElement(child, child.props, applyPaddingToFirstTd(child.props.children, level, found));
            }
            return child;
        }
        
        if (typeof child.type === 'string' && child.type.toLowerCase() === 'tr') {
            return React.cloneElement(child, child.props, applyPaddingToFirstTd(child.props.children, level, found));
        }
        // If child is a td
        if (typeof child.type === 'string' && child.type.toLowerCase() === 'td') {
            found.value = true;
            
            const padding = level > 0 ? 7.2 * Math.pow(2, level - 1) : 0;
            return React.cloneElement(child, {
                style: { ...(child.props.style || {}), paddingLeft: `${padding}rem` }
            });
        }
        if (child.props && child.props.children) {
            return React.cloneElement(child, child.props, applyPaddingToFirstTd(child.props.children, level, found));
        }
        return child;
    });
}

function Expanded({ children, level = 1 }) {
    const paddedChildren = React.Children.map(children, child => {
        
        if (child && child.type === Row) {
            return React.cloneElement(child, { level });
        }
        
        return applyPaddingToFirstTd([child], level)[0];
    });
    return <>{paddedChildren}</>;
}

function ExpandableTableBody ({children}) {
    return (
        <tbody>
            {children}
        </tbody>
    );
}

ExpandableTable.Body = ExpandableTableBody;
Row.Expandable = Expandable;
Row.Expanded = Expanded;
ExpandableTable.Row = Row;
